import { RiskAssesmentItem } from "../../../api";
import "./RiskAssesmentCard.css"
import { useState, useEffect } from "react";
import { Row, Col, Form } from "react-bootstrap";
import StbButton from "../../atoms/button/StbButton";

export interface RiskAssesmentItemProp {
  riskAssesmentItem: RiskAssesmentItem;
  radioGroupIndex: number;
  onUpdate: (updatedData: RiskAssesmentItem) => void;
  isWorkerView: boolean;
  canEditName?: boolean;
  onRemove?: () => void;
}

const RiskAssesmentCardItem: React.FC<RiskAssesmentItemProp> = (props) => {

  const [isScreenSmall, setIsScreenSmall] = useState(window.innerWidth <= 1026);

  useEffect(() => {
    const handleResize = () => {
      setIsScreenSmall(window.innerWidth <= 1026);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Helper function to handle radio button changes
  const handleRadioChange = (selectedKey: 'noExtent' | 'smallExtent' | 'highExtent') => {
    const currentItem = props.riskAssesmentItem;
    const isWorkerView = props.isWorkerView;

    let changeAllowed = true;

    // Determine if the change is allowed in worker view
    if (isWorkerView) {
      // Get current risk level based on checked properties
      const currentNoExtent = currentItem.noExtent ?? false;
      const currentSmallExtent = currentItem.smallExtent ?? false;
      const currentHighExtent = currentItem.highExtent ?? false;

      if (selectedKey === 'noExtent') {
        // Allow setting to noExtent only if currently no box is checked
        changeAllowed = !currentNoExtent && !currentSmallExtent && !currentHighExtent;
      } else if (selectedKey === 'smallExtent') {
        // Allow setting to smallExtent only if currently no box is checked or noExtent is checked
        changeAllowed = (!currentNoExtent && !currentSmallExtent && !currentHighExtent) || currentNoExtent;
      } else if (selectedKey === 'highExtent') {
        // Always allow setting to highExtent in worker view (increases or maintains highest risk)
        changeAllowed = true;
      }
    }

    if (changeAllowed) {
      const updatedItem = { ...currentItem };

      updatedItem.noExtent = selectedKey === 'noExtent';
      updatedItem.smallExtent = selectedKey === 'smallExtent';
      updatedItem.highExtent = selectedKey === 'highExtent';

      props.onUpdate(updatedItem);
    }
  };

  // Helper function to handle text input changes
  const handleTextChange = (key: 'personalSafetyEquipment' | 'riskForIllnes', value: string) => {
    const updatedItem = { ...props.riskAssesmentItem, [key]: value };
    props.onUpdate(updatedItem);
  };

  const handleNameChange = (value: string) => {
    props.onUpdate({ ...props.riskAssesmentItem, name: value });
  };

  const renderRiskNameEditor = () => (
    <div className="d-flex gap-2 align-items-center">
      <Form.Control
        aria-label="Arbetsmiljörisk"
        value={props.riskAssesmentItem?.name ?? ''}
        onChange={(event) => handleNameChange(event.target.value)}
        placeholder="Beskriv risken"
      />
      {props.onRemove && (
        <StbButton type="button" variant="danger" size="sm" onClick={props.onRemove} aria-label="Ta bort risk">
          Ta bort
        </StbButton>
      )}
    </div>
  );

  return (
    <div>
      {!isScreenSmall && (
        <Row className="rowSize">
          <Col xs={8} sm={6} md={5} lg={4} xl={3} className="textTitle">
            {props.canEditName ? renderRiskNameEditor() : props.riskAssesmentItem?.name}
          </Col>
          <Col xs={6} sm={4} md={3} lg={2} xl={1}>
            <Form.Check
              checked={props.riskAssesmentItem.noExtent ?? false}
              onChange={() => handleRadioChange('noExtent')}
              type="radio"
              inline
              name={props.radioGroupIndex?.toString()}
              id={`inteAlls-${props.radioGroupIndex}`}
              className="custom-radio-inte-alls"
              disabled={props.isWorkerView && ((props.riskAssesmentItem.smallExtent ?? false) || (props.riskAssesmentItem.highExtent ?? false))}
            />
          </Col>
          <Col xs={6} sm={4} md={3} lg={2} xl={1}>
            <Form.Check
              checked={props.riskAssesmentItem.smallExtent ?? false}
              onChange={() => handleRadioChange('smallExtent')}
              type="radio"
              inline
              name={props.radioGroupIndex?.toString()}
              id={`ILitenOmfattning-${props.radioGroupIndex}`}
              className="custom-radio-i-liten-omfattning"
              disabled={props.isWorkerView && (props.riskAssesmentItem.highExtent ?? false)}
            />
          </Col>
          <Col xs={6} sm={4} md={3} lg={2} xl={1}>
            <Form.Check
              checked={props.riskAssesmentItem.highExtent ?? false}
              onChange={() => handleRadioChange('highExtent')}
              type="radio"
              inline
              name={props.radioGroupIndex?.toString()}
              id={`IHögOmfattning-${props.radioGroupIndex}`}
              className="custom-radio-i-hög-omfattning"
            />
          </Col>
          <Col xs={8} sm={6} md={5} lg={4} xl={3}>
            <Form.Control
              value={props.riskAssesmentItem.personalSafetyEquipment ?? ""}
              onChange={(event) => handleTextChange('personalSafetyEquipment', event.target.value)}
              className="riskAssesmentformControl"
              type="text"
              id={`skyddsutrustning-${props.radioGroupIndex}`}
              placeholder="Kommentar" />
          </Col>
          <Col xs={8} sm={6} md={5} lg={4} xl={3}>
            <Form.Control
              value={props.riskAssesmentItem.riskForIllnes ?? ""}
              onChange={(event) => handleTextChange('riskForIllnes', event.target.value)}
              className="riskAssesmentformControl"
              type="text"
              id={`riskFörOhälsa-${props.radioGroupIndex}`}
              placeholder="Kommentar" />
          </Col>
        </Row>
      )}
      {isScreenSmall && (
        <div>
          {props.canEditName && (
            <div className="mb-3">{renderRiskNameEditor()}</div>
          )}
          <Row>
            <Col xs={8}>
              <Form.Label>Inte alls </Form.Label>
            </Col>
            <Col>
              <Form.Check
                checked={props.riskAssesmentItem.noExtent ?? false}
                onChange={() => handleRadioChange('noExtent')}
                type="radio"
                inline
                name={props.radioGroupIndex?.toString()}
                id={`inteAllsSmall-${props.radioGroupIndex}`}
                className="custom-radio-inte-alls" />
            </Col>
          </Row>
          <Row>
            <Col xs={8}>
              <Form.Label>I liten omfattning</Form.Label>
            </Col>
            <Col>
              <Form.Check
                checked={props.riskAssesmentItem.smallExtent ?? false}
                onChange={() => handleRadioChange('smallExtent')}
                type="radio"
                inline
                name={props.radioGroupIndex?.toString()}
                id={`ILitenOmfattningSmall-${props.radioGroupIndex}`}
                className="custom-radio-i-liten-omfattning" />
            </Col>
          </Row>
          <Row>
            <Col xs={8}>
              <Form.Label>I hög omfattning</Form.Label>
            </Col>
            <Col>
              <Form.Check
                checked={props.riskAssesmentItem.highExtent ?? false}
                onChange={() => handleRadioChange('highExtent')}
                type="radio"
                inline
                name={props.radioGroupIndex?.toString()}
                id={`IHögOmfattningSmall-${props.radioGroupIndex}`}
                className="custom-radio-i-hög-omfattning" />
            </Col>
          </Row>
          <Col>
            <Form.Label>Personlig skyddsutrustning som används</Form.Label>
            <Form.Control
              value={props.riskAssesmentItem.personalSafetyEquipment ?? ""}
              onChange={(event) => handleTextChange('personalSafetyEquipment', event.target.value)}
              className="riskAssesmentformControl"
              type="text"
              id={`skyddsutrustningSmall-${props.radioGroupIndex}`}
              placeholder="Kommentar" />
          </Col>
          <Col>
            <Form.Label>Vilka åtgärder behöver vidtas för att minska eller eliminera risk för ohälsa</Form.Label>
            <Form.Control
              value={props.riskAssesmentItem?.riskForIllnes ?? ""}
              onChange={(event) => handleTextChange('riskForIllnes', event.target.value)}
              className="riskAssesmentformControl"
              type="text"
              id={`riskFörOhälsaSmall-${props.radioGroupIndex}`}
              placeholder="Kommentar" />
          </Col>
        </div>
      )}
    </div>
  )
}

export default RiskAssesmentCardItem;
